/**
 * Centralized Clumse → Discord activity pipeline (via POST /api/public/activity).
 *
 * - Non-blocking: work is scheduled with queueMicrotask + fetch (fire-and-forget).
 * - Enriches payloads with session id, device, viewport, URLs, navigation timing.
 *
 * Mixpanel uses `trackEvent` from helpers/mixpanel - this module exports
 * `trackActivityEvent` to avoid naming collisions.
 */

import { getAuth } from "firebase/auth";
import {
  getActivitySessionId,
  getDeviceType,
  getViewportSummary,
} from "./activityContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

const BATCH_IDLE_MS = 90_000;

let _pageViewBuffer = [];
let _idleTimer = null;

let _routeEnterTime = typeof Date !== "undefined" ? Date.now() : 0;
let _routeKey = "";

/**
 * Call on each SPA navigation before enqueueing page_view. Updates internal
 * clock and returns dwell time on the *previous* route.
 */
export function navigationTick(pathnameAndSearch) {
  const now = Date.now();
  const prevKey = _routeKey;
  const dwellMs =
    prevKey && prevKey !== pathnameAndSearch ? now - _routeEnterTime : undefined;
  _routeEnterTime = now;
  _routeKey = pathnameAndSearch;
  return {
    previousPath: prevKey || undefined,
    timeOnPreviousPageMs: dwellMs,
  };
}

function getUtmSummary() {
  if (typeof window === "undefined") return undefined;
  try {
    const p = new URLSearchParams(window.location.search);
    const keys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
    ];
    const parts = [];
    for (const k of keys) {
      const v = p.get(k);
      if (v) parts.push(`${k}=${decodeURIComponent(v)}`);
    }
    return parts.length ? parts.join(" · ") : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Deep-clone to JSON-safe data only (drops DOM, Events, class instances, cycles).
 * Used for `metadata` and for the full POST body so JSON.stringify never throws.
 */
function deepSanitizeForJson(raw, depth = 0) {
  if (raw == null) return undefined;
  if (depth > 10) return undefined;
  const t = typeof raw;
  if (t === "string" || t === "number" || t === "boolean") return raw;
  if (t === "bigint") return String(raw);
  if (t === "function" || t === "symbol") return undefined;
  if (typeof raw === "object") {
    if (typeof Node !== "undefined" && raw instanceof Node) return undefined;
    if (typeof Event !== "undefined" && raw instanceof Event) return undefined;
    if (raw instanceof Date) return raw.toISOString();
    if (Array.isArray(raw)) {
      const arr = raw
        .map((x) => deepSanitizeForJson(x, depth + 1))
        .filter((x) => x !== undefined);
      return arr.length ? arr : undefined;
    }
    const proto = Object.getPrototypeOf(raw);
    if (proto !== null && proto !== Object.prototype) return undefined;
    const out = {};
    for (const k of Object.keys(raw)) {
      const v = deepSanitizeForJson(raw[k], depth + 1);
      if (v !== undefined) out[k] = v;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
}

function getClientEnvironment() {
  if (typeof window === "undefined") return {};
  const nav = navigator;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  let timeZone;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }
  const langs =
    Array.isArray(nav.languages) && nav.languages.length > 0
      ? nav.languages.slice(0, 6).join(", ")
      : undefined;
  return {
    pageTitle:
      typeof document !== "undefined" && document.title
        ? String(document.title).trim() || undefined
        : undefined,
    language: nav.language || undefined,
    languages: langs,
    timeZone,
    screen:
      typeof screen !== "undefined"
        ? `${screen.width}×${screen.height}`
        : undefined,
    pixelRatio:
      typeof window.devicePixelRatio === "number"
        ? window.devicePixelRatio
        : undefined,
    networkEffectiveType: conn?.effectiveType || undefined,
    saveData: typeof conn?.saveData === "boolean" ? conn.saveData : undefined,
    utmSummary: getUtmSummary(),
  };
}

function buildRichPayload(event, metadata = {}) {
  const user = getAuth().currentUser;
  const href =
    typeof window !== "undefined" ? window.location.href : undefined;
  const path =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : undefined;
  const env = getClientEnvironment();
  const safeMetadata = deepSanitizeForJson(metadata);

  return {
    event,
    loggedIn: Boolean(user),
    userId: user?.uid ?? undefined,
    userEmail: user?.email ?? undefined,
    page: path,
    currentUrl: href,
    referrer:
      typeof document !== "undefined"
        ? document.referrer || undefined
        : undefined,
    referralUrl:
      typeof document !== "undefined"
        ? document.referrer || undefined
        : undefined,
    sessionId: getActivitySessionId(),
    deviceType: getDeviceType(),
    viewport: getViewportSummary(),
    clientTimestamp: new Date().toISOString(),
    pageTitle: env.pageTitle,
    language: env.language,
    languages: env.languages,
    timeZone: env.timeZone,
    screen: env.screen,
    pixelRatio: env.pixelRatio,
    networkEffectiveType: env.networkEffectiveType,
    saveData: env.saveData,
    utmSummary: env.utmSummary,
    metadata:
      safeMetadata && Object.keys(safeMetadata).length > 0
        ? safeMetadata
        : undefined,
  };
}

function schedulePost(body) {
  if (!body || typeof body !== "object") return;
  const safeBody = deepSanitizeForJson(body) ?? {};
  let bodyStr;
  try {
    bodyStr = JSON.stringify(safeBody);
  } catch {
    try {
      bodyStr = JSON.stringify({
        event: typeof body?.event === "string" ? body.event : "activity_serialize_error",
        clientTimestamp: new Date().toISOString(),
        metadata: { note: "payload_omitted_non_serializable" },
      });
    } catch {
      return;
    }
  }
  try {
    queueMicrotask(() => {
      fetch(`${API_BASE}/public/activity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyStr,
        keepalive: true,
      }).catch(() => {});
    });
  } catch {
    fetch(`${API_BASE}/public/activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: bodyStr,
      keepalive: true,
    }).catch(() => {});
  }
}

function _resetIdleTimer() {
  if (_idleTimer) clearTimeout(_idleTimer);
  _idleTimer = setTimeout(_flushPageViews, BATCH_IDLE_MS);
}

function _flushPageViews() {
  if (_idleTimer) {
    clearTimeout(_idleTimer);
    _idleTimer = null;
  }

  const batch = _pageViewBuffer.slice();
  _pageViewBuffer = [];

  if (batch.length === 0) return;

  const dwellSummary = batch
    .map((v) => {
      if (v.timeOnPreviousPageMs == null) return null;
      return `${v.previousPath || "?"} → ${v.path}: ${Math.round(
        v.timeOnPreviousPageMs / 1000
      )}s`;
    })
    .filter(Boolean);

  const body = {
    ...buildRichPayload("page_views"),
    page: batch[0]?.path,
    metadata: {
      pages: batch.map((v) => v.path),
      count: batch.length,
      duration_s:
        batch.length > 1
          ? Math.round((batch[batch.length - 1].ts - batch[0].ts) / 1000)
          : 0,
      dwell_segments: dwellSummary.slice(0, 12),
    },
  };

  schedulePost(body);
}

if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") _flushPageViews();
  });
  window.addEventListener("pagehide", _flushPageViews);
  window.addEventListener("beforeunload", _flushPageViews);
}

/**
 * Primary API: send a single activity event (Discord embed via backend).
 *
 * @param {string} event - machine name, e.g. ui_interaction, error_404, demo_message
 * @param {Record<string, unknown>} [metadata]
 */
export function trackActivityEvent(event, metadata = {}) {
  try {
    if (event === "page_view") {
      const nav = navigationTick(
        window.location.pathname + window.location.search
      );
      _pageViewBuffer.push({
        path: window.location.pathname + window.location.search,
        ts: Date.now(),
        previousPath: nav.previousPath,
        timeOnPreviousPageMs: nav.timeOnPreviousPageMs,
      });
      _resetIdleTimer();
      return;
    }

    const base = buildRichPayload(event, metadata);
    schedulePost(base);
  } catch {
    /* never throw */
  }
}

let _listenersInstalled = false;

function inferClickIntent(el) {
  if (!(el instanceof Element)) return null;
  const explicit = el.closest("[data-activity]")?.getAttribute("data-activity");
  if (explicit) return explicit;

  const link = el.closest("a[href]");
  if (link) {
    const href = (link.getAttribute("href") || "").toLowerCase();
    if (
      href.includes("/signup") ||
      href.includes("signup") ||
      href.includes("plan=free")
    )
      return "signup";
    if (href.includes("pricing") || href.includes("#pricing"))
      return "pricing";
    if (href.includes("/login")) return "login";
  }

  const btn = el.closest("button,[role='button']");
  const root = btn || el;
  const text = (root.textContent || "").trim().replace(/\s+/g, " ");
  const t = text.toLowerCase();
  if (/sign\s*up|get started|start free|create account|register/.test(t))
    return "signup";
  if (/pricing|see plans|view plans|plans/.test(t)) return "pricing";
  if (/log\s*in|sign\s*in/.test(t)) return "login";
  return null;
}

function textPreview(el, max = 80) {
  try {
    const t = (el.textContent || "").trim().replace(/\s+/g, " ");
    return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
  } catch {
    return "";
  }
}

export function installGlobalActivityListeners() {
  if (typeof window === "undefined" || _listenersInstalled) return;
  _listenersInstalled = true;

  document.addEventListener(
    "click",
    (e) => {
      const intent = inferClickIntent(e.target);
      if (!intent) return;
      const el = e.target instanceof Element ? e.target : null;
      trackActivityEvent("ui_interaction", {
        kind: "click",
        intent,
        tag: el?.tagName,
        preview: el ? textPreview(el.closest("a,button,[role='button']") || el) : "",
      });
    },
    true
  );

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      const id = form instanceof HTMLFormElement ? form.id || "" : "";
      const name = form instanceof HTMLFormElement ? form.name || "" : "";
      trackActivityEvent("form_submit", {
        formId: id || undefined,
        formName: name || undefined,
        action:
          form instanceof HTMLFormElement
            ? (form.getAttribute("action") || "").slice(0, 200)
            : undefined,
      });
    },
    true
  );

  // ── Noise filters ─────────────────────────────────────────────────────────
  // Patterns that are definitively NOT Clumse application errors.  They come
  // from browser extensions, browser internals, or third-party injected scripts
  // and would pollute the Discord feed with hundreds of false positives.
  //
  // "Object Not Found Matching Id:N, MethodName:update, ParamCount:4"
  //   → Chrome extension internal API (ports / native-messaging channels) being
  //     garbage-collected while an async operation is still pending.  Fired by
  //     extensions such as Grammarly, LastPass, uBlock, etc.
  const BROWSER_NOISE_RE =
    /Object Not Found Matching Id:\d+|ResizeObserver loop|Loading chunk \d+ failed|^Script error\.?$|Non-Error promise rejection captured|chrome-extension:|moz-extension:/i;

  window.addEventListener("error", (e) => {
    const msg = e.message || "error";
    if (BROWSER_NOISE_RE.test(msg)) return;
    // Also drop errors whose filename points to a browser extension
    const src = e.filename || "";
    if (/^(chrome|moz|safari)-extension:/.test(src)) return;
    trackActivityEvent("client_error", {
      message: String(msg).slice(0, 300),
      source: src ? String(src).slice(0, 200) : undefined,
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const msg =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
        ? reason
        : String(reason);
    if (BROWSER_NOISE_RE.test(msg)) return;
    // Drop rejections whose stack points entirely at a browser extension
    const stack = reason instanceof Error ? (reason.stack || "") : "";
    if (/^(chrome|moz|safari)-extension:/.test(stack.split("\n")[1] || "")) return;
    trackActivityEvent("client_error", {
      message: `Unhandled rejection: ${msg}`.slice(0, 400),
      source: "unhandledrejection",
    });
  });
}
